import { getLoggerInstance } from '@idmc-ms-fabric/common';
import { WorkloadClientAPI } from '@idmc-ms-fabric/integration';
import { fabricServiceModels } from '@infa-hawk/services';

import { TimeoutID } from '../../../utils/common-types';
import constants from '../../../utils/constants';
import { delay, devMode, fetchWrapper } from '../../../utils/coreUtils';
import { getCookie } from '../../../utils/infa-utils';
import { LocalStorage, SessionStorage } from '../../../utils/storage-utils';
import { getTracingHeaders } from '../../../utils/trace-utils';
import {
	ServiceDiscoveryResponse,
	TenantStatus,
	TLoginLocalStorageDetails,
	TLoginServiceDiscovery,
	TLoginSessionStorageDetails,
	TSerializedAccessToken,
} from './InfaLogin.type';
import InfaTenantStatusPolling from './InfaTenantStatusPolling';

const IDS_SESSION_HEADERS_URLS: Array<string> = [];
export type BootoutType = {
	serviceBaseURLs: Record<string, string>;
	fetchApi: typeof fetch;
	tenantId: string;
	status: TenantStatus;
	sessionId: string;
	jwtToken: string;
	fabricToken: string;
	orgId: string;
};

class InfaLoginManager {
	private STORAGE_KEY = 'INFA_LOGIN_MANAGER';
	private baseURL = devMode ? import.meta.env.VITE_PROXY_URL : '';
	private _polling!: InfaTenantStatusPolling;
	private _timeout!: TimeoutID;
	private _bootoutput!: BootoutType;
	private _urlsWithIDSSessionHeaders: Array<string> = [];
	constructor(
		private extensionClient: WorkloadClientAPI,
		private loginScope: string,
		private workspaceId: string,
		private callback: (config: BootoutType) => void,
	) {}

	public async init() {
		const logger = getLoggerInstance({});
		this.cleanUp();

		//Get azure authentication token
		const { token: fabricToken } = await this.getFabricAuthToken();

		//Get JWT token from session or by exchanging azure token
		const sessionDetails = SessionStorage.get<TLoginSessionStorageDetails>(this.STORAGE_KEY);
		if (!sessionDetails.infaAuthDetails || this.hasExpired(sessionDetails.infaAuthDetails.expiry)) {
			const infaAuthDetails = await this.getInfaAuthDetails(fabricToken);
			logger.info('Fetched Informatica JWT token/ OrgId ');
			sessionDetails.infaAuthDetails = { ...infaAuthDetails, expiry: this.getExpiryFromJWT(infaAuthDetails.jwtToken) };
			SessionStorage.set<TLoginSessionStorageDetails>(this.STORAGE_KEY, sessionDetails);
		}
		const { orgId, sessionId, jwtToken } = sessionDetails.infaAuthDetails;

		// Service discovery
		const { serviceBaseURLs, tenantGroupId, status, tenantId } = await this.getServiceDetails(jwtToken, orgId);
		this.setUrlsWithIDSSessionHeader(serviceBaseURLs);
		this._bootoutput = {
			serviceBaseURLs,
			fetchApi: this.getFetchApi(tenantGroupId),
			tenantId,
			status,
			sessionId,
			jwtToken,
			orgId,
			fabricToken,
		};

		/**
		 * If tenant not active poll until it is active.
		 */
		if (status !== 'Active') {
			logger.info('Tenant status is not Active.', { status });
			this._polling = new InfaTenantStatusPolling(() => this.fetchServiceDiscovery(jwtToken, tenantId), {
				interval: 10 * 1000,
				onChange: ({ status }) => {
					if (status === 'Active') {
						logger.info('Tenant statue is Active.');
						this._polling.stop();
					}
					this.callback({ ...this._bootoutput, status });
				},
			});
			this._polling.start();
		}
		return this._bootoutput;
	}
	private setUrlsWithIDSSessionHeader(serviceBaseURLs: Record<string, string>) {
		Object.entries(serviceBaseURLs).forEach(([key, value]) => {
			if (IDS_SESSION_HEADERS_URLS.includes(key)) {
				this._urlsWithIDSSessionHeaders.push(value);
			}
		});
	}
	private fetchApi(tenantGroupId: string, url: URL | RequestInfo, options: RequestInit | undefined): Promise<Response> {
		const { jwtToken, sessionId, tenantId } = this._bootoutput;
		const requiredHeaders: Record<string, string> = {
			'X-INFA-ORG-ID': tenantId,
			'X-INFA-TG-ID': tenantGroupId,
			'X-INFA-SESSION-ID': sessionId,
			'Authorization': `Bearer ${jwtToken}`,
			...getTracingHeaders(),
		};

		const xsrfToken = getCookie('XSRF_TOKEN');
		if (xsrfToken) {
			requiredHeaders.XSRF_TOKEN = xsrfToken;
		}

		if (this._urlsWithIDSSessionHeaders.length > 0) {
			this._urlsWithIDSSessionHeaders.forEach((value) => {
				if (url.toString().search(value) !== -1) {
					requiredHeaders['IDS-SESSION-ID'] = sessionId;
				}
			});
		}
		return fetch(url, {
			...options,
			credentials: 'include',
			headers: {
				...options?.headers,
				...requiredHeaders,
			},
		});
	}

	private async reinit(tenantGroupId: string, url: URL | RequestInfo, options?: RequestInit): Promise<Response> {
		try {
			await this.init();
			return await this.fetchApi(tenantGroupId, url, options);
		} catch (error) {
			await delay(2000);
			return await this.reinit(tenantGroupId, url, options);
		}
	}
	private getFetchApi(tenantGroupId: string): typeof fetch {
		return async (url, options) => {
			const { jwtToken } = this._bootoutput;
			const logger = getLoggerInstance({});
			try {
				const response = await this.fetchApi(tenantGroupId, url, options);
				/**
				 * Re-initaliizing the boolader with new token
				 * incase of 401 status
				 */
				if (!response.ok) {
					logger.error('fetch failed', { status: response.status, url, method: options?.method });
					throw new Error('fetch failed');
				}
				return response;
			} catch (error: any) {
				/**
				 * Re-initaliizing the boolader with new token
				 * incase of 401 status
				 */
				const isJwtExpired: boolean = this.hasExpired(this.getExpiryFromJWT(jwtToken));
				if (isJwtExpired) {
					return await this.reinit(tenantGroupId, url, options);
				}
				logger.error('Failed to fetch', { url, method: options?.method });
				throw new Error(error);
			}
		};
	}

	private async getInfaAuthDetails(accessToken: string) {
		return await fetchWrapper.post<fabricServiceModels.IGenerateTokenRequest, fabricServiceModels.IGenerateTokenResponse>(
			`${constants.FABRIC_BE_SERVICE_URI}/api/v1/authentication/generateToken`,
			{
				accessToken,
				workspaceId: this.workspaceId,
			},
		);
	}
	private decodeJWT<T>(token: string): T {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map(function (c) {
					return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
				})
				.join(''),
		);
		const decodedToken = JSON.parse(jsonPayload) as T;
		return decodedToken;
	}
	private getExpiryFromJWT(token: string): number {
		const decodedToken = this.decodeJWT<{ exp: number }>(token);
		return decodedToken.exp * 1000;
	}
	private getUserIdFromJWT(token: string): string {
		const decodedToken = this.decodeJWT<{ user_id: string }>(token);
		return decodedToken.user_id;
	}
	private hasExpired(expiryDate?: number) {
		return expiryDate ? expiryDate - Date.now() <= 0 : true;
	}

	private async getAzureAccessToken(): Promise<TSerializedAccessToken> {
		const logger = getLoggerInstance({});
		try {
			const bootdetails = LocalStorage.get<TLoginLocalStorageDetails>(this.STORAGE_KEY);
			const fabricConsentAccepted = bootdetails && bootdetails.fabric_consent;
			const { token, expiry } = await this.extensionClient.auth.acquireAccessToken({
				additionalScopesToConsent: fabricConsentAccepted ? undefined : [this.loginScope],
			});
			if (!fabricConsentAccepted) {
				LocalStorage.set<TLoginLocalStorageDetails>(this.STORAGE_KEY, {
					fabric_consent: true,
				});
			}
			logger.info('Acquired Microsoft Auth access token');
			return { token, expiry: expiry instanceof Date ? expiry.getTime() : undefined };
		} catch (error) {
			logger.error('Failed to acquire Microsoft Auth access token');
			throw error;
		}
	}
	private async getFabricAuthToken(): Promise<TSerializedAccessToken> {
		let token: TSerializedAccessToken;
		const sessionDetails = SessionStorage.get<TLoginSessionStorageDetails>(this.STORAGE_KEY);
		if (sessionDetails && sessionDetails.token) {
			if (!sessionDetails.token.expiry || this.hasExpired(sessionDetails.token.expiry)) {
				token = await this.getAzureAccessToken();
			} else {
				token = sessionDetails.token;
			}
		} else {
			token = await this.getAzureAccessToken();
		}

		SessionStorage.set<TLoginSessionStorageDetails>(this.STORAGE_KEY, { ...sessionDetails, token });
		if (this._timeout) {
			clearTimeout(this._timeout);
		}
		if (token.expiry) {
			this._timeout = setTimeout(async () => {
				const fabricToken = await this.getFabricAuthToken();
				this.callback({ ...this._bootoutput, fabricToken: fabricToken.token });
			}, token.expiry - Date.now());
		}

		return token;
	}

	private async fetchServiceDiscovery(jwtToken: string, tenantId: string) {
		return await fetchWrapper.get<ServiceDiscoveryResponse>(`${this.baseURL}/ccgf-service-discovery/api/v1/tms/servicemappingsWithPodInfo/${tenantId}`, {
			Authorization: `Bearer ${jwtToken}`,
		});
	}

	private async getServiceDetails(jwtToken: string, tenantId: string): Promise<TLoginServiceDiscovery> {
		const logger = getLoggerInstance({});
		const { serviceDiscovery, ...storageRest } = SessionStorage.get<TLoginSessionStorageDetails>(this.STORAGE_KEY);
		if (!serviceDiscovery) {
			const { serviceDetails, ...rest } = await this.fetchServiceDiscovery(jwtToken, tenantId);
			logger.info('Fetched Service Details');
			const serviceBaseURLs: Record<string, string> = serviceDetails.reduce((o, x) => ({ ...o, [x.serviceName]: x.serviceVersion.externalServiceUrl }), {});
			const newServiceDiscovery = {
				serviceBaseURLs,
				...rest,
			};
			SessionStorage.set<TLoginSessionStorageDetails>(this.STORAGE_KEY, { serviceDiscovery: newServiceDiscovery, ...storageRest });
			return newServiceDiscovery;
		}
		return serviceDiscovery;
	}

	public cleanUp() {
		if (this._polling) {
			this._polling.stop();
		}
		if (this._timeout) {
			clearTimeout(this._timeout);
		}
	}
}

export default InfaLoginManager;
